import { inject, Injectable } from '@angular/core';
import { HttpGatewayService } from './http-gateway.service';
import { Observable } from 'rxjs';
import { UserCreateInviteContract, UserInviteContract, UserInviteContractV3 } from '../../shared/types/user';
import { UrlBuilder } from '../../shared/utils/url-utils';
import { InviteSendResult } from '../../shared/types/registration';

const REGISTRATION_URLS = {
    GET_INVITATIONS: 'api/v5/admin/users/invites/{organizationId}',
    GET_INVITATIONS_V3: 'api/admin/users/invites/{chainId}',
    GET_INVITATION: 'api/v5/admin/users/invite/{inviteId}',
    UPDATE_INVITATION: 'api/v5/admin/users/invite/{inviteId}',
    SEND_INVITE: 'api/v5/admin/users/invite',
    RESEND_INVITE: 'api/v5/admin/users/invite/{inviteId}/resend',
    CANCEL_INVITE: 'api/v5/admin/users/invite/{inviteId}/cancel',
    USER_EXISTS: 'registration/v5/user-exists',
    INVITE_EXISTS: 'registration/v5/invite-exists',
    COMPLETE_REGISTRATION: 'registration/v5/create-user',
};

@Injectable({ providedIn: 'root' })
export class RegistrationService {
    private http = inject(HttpGatewayService);

    public userExists(email: string): Observable<boolean> {
        const url = new UrlBuilder(REGISTRATION_URLS.USER_EXISTS).addQueryParameter('email', email).toUrl();
        return this.http.get(url);
    }

    public sendInvitations(invites: UserCreateInviteContract[]): Observable<InviteSendResult[]> {
        return this.http.post(REGISTRATION_URLS.SEND_INVITE, invites);
    }

    public validateInvitation(inviteId: string): Observable<{ exists: boolean }> {
        const urlBuilder = new UrlBuilder(REGISTRATION_URLS.INVITE_EXISTS);
        urlBuilder.addQueryParameter('id', inviteId);
        return this.http.get(urlBuilder.toUrl());
    }

    public completeRegistration(inviteId: string, password: string): Observable<{ success: boolean }> {
        return this.http.post(REGISTRATION_URLS.COMPLETE_REGISTRATION, {
            inviteId,
            password,
        });
    }

    public getInvitation(inviteId: string): Observable<UserInviteContract> {
        return this.http.get(new UrlBuilder(REGISTRATION_URLS.GET_INVITATION).addInviteId(inviteId).toUrl());
    }

    public updateInvitation(inviteId: string, contract: UserCreateInviteContract): Observable<{ success: boolean }> {
        return this.http.put(
            new UrlBuilder(REGISTRATION_URLS.UPDATE_INVITATION).addInviteId(inviteId).toUrl(),
            contract,
        );
    }

    public getPendingInvitations(organizationId: string): Observable<UserInviteContract[]> {
        return this.http.get(
            new UrlBuilder(REGISTRATION_URLS.GET_INVITATIONS).addOrganizationId(organizationId).toUrl(),
        );
    }

    public getPendingV3Invitations(chainId: number): Observable<UserInviteContractV3[]> {
        return this.http.get(
            new UrlBuilder(REGISTRATION_URLS.GET_INVITATIONS_V3).addChainId(chainId).toUrl(),
        );
    }

    public resendInvitation(inviteId: string): Observable<UserInviteContract> {
        return this.http.post(new UrlBuilder(REGISTRATION_URLS.RESEND_INVITE).addInviteId(inviteId).toUrl(), {});
    }

    public cancelInvitation(inviteId: string): Observable<UserInviteContract> {
        return this.http.post(new UrlBuilder(REGISTRATION_URLS.CANCEL_INVITE).addInviteId(inviteId).toUrl(), {});
    }
}
